import { safeGet } from '../../../core/utils/safe-get.js';
import type {
  XYChartColorScale,
  AnyXYChartScale,
  WeakMapDatapoints,
  RectDatapointInternal,
} from '../../types/xy-chart-internals.js';
import { getColorFromScaleAndData } from '../../utils/get-color-from-scale-and-data.js';
import {
  isCategoricalRectScale,
  isNumericalRectScale,
  isTimeRectScale,
} from '../../utils/type-guards.js';

export function colorAccessor(colorScale: XYChartColorScale) {
  return (datapoint: RectDatapointInternal) =>
    // At this point we know value can not be null, undefined nor NaN, hence it will not be painted in the canvas
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    getColorFromScaleAndData(colorScale, datapoint.value!);
}

export function xAccessor(scale: AnyXYChartScale) {
  return (datapoint: RectDatapointInternal) => {
    const args = { scale, v0: datapoint.x0, v1: datapoint.x1 };

    if (isCategoricalRectScale(args)) {
      return args.scale(args.v0) ?? 0;
    } else if (isNumericalRectScale(args) || isTimeRectScale(args)) {
      const { scale, v0, v1 } = args;

      return Math.min(scale(v0), scale(v1));
    }

    throw Error('unknown xAccessor value for Rect');
  };
}

export function yAccessor(scale: AnyXYChartScale) {
  return (datapoint: RectDatapointInternal) => {
    const args = { scale, v0: datapoint.y0, v1: datapoint.y1 };

    if (isCategoricalRectScale(args)) {
      return args.scale(args.v0) ?? 0;
    } else if (isNumericalRectScale(args) || isTimeRectScale(args)) {
      const { scale, v0, v1 } = args;

      return Math.min(scale(v0), scale(v1));
    }
    throw Error('unknown yAccessor value for Rect');
  };
}

export function widthAccessor(scale: AnyXYChartScale) {
  return (datapoint: RectDatapointInternal) => {
    const args = { scale, v0: datapoint.x0, v1: datapoint.x1 };

    if (isNumericalRectScale(args) || isTimeRectScale(args)) {
      const { scale, v0, v1 } = args;
      return Math.abs(scale(v1) - scale(v0));
    } else if (isCategoricalRectScale(args)) {
      return args.scale.bandwidth();
    }

    throw Error('unknown widthAccessor value for Rect');
  };
}

export function heightAccessor(scale: AnyXYChartScale) {
  return (datapoint: RectDatapointInternal) => {
    const args = { scale, v0: datapoint.y0, v1: datapoint.y1 };

    if (isNumericalRectScale(args) || isTimeRectScale(args)) {
      const { scale, v0, v1 } = args;
      return Math.abs(scale(v1) - scale(v0));
    } else if (isCategoricalRectScale(args)) {
      return args.scale.bandwidth();
    }

    throw Error('unknown heightAccessor value for Rect');
  };
}

export function isDefinedAccessor(datapointMetadataMap: WeakMapDatapoints) {
  return (datapoint: RectDatapointInternal) =>
    !safeGet(datapointMetadataMap, datapoint).isGap;
}
